import { mapLocaleToStoreCode } from '@/constants/market'
import { getMarketBaseURL } from '@/lib/configs'
import { API_GET_MARKET_PLAN_LIST } from '@/services/api'
import { TipAmountResponse } from '@/types/market'
import { Locale } from 'next-intl'

const buildGraphqlGetUrl = (
  basePath: string,
  query: string,
  variables: Record<string, unknown>,
) => {
  const qs = new URLSearchParams({
    query,
    variables: JSON.stringify(variables),
  })
  return `${basePath}?${qs.toString()}`
}

export const fetchMarketAPI = <Response = Record<string, unknown>>(
  url: string,
  method: RequestInit['method'] = 'GET',
  payload: RequestInit['body'],
  params: Omit<RequestInit, 'method' | 'body'> = {},
  htcToken = '',
): Promise<{ status: number; results: Response; headers: Headers } | void> => {
  const init: RequestInit = {
    headers: { 'content-type': 'application/json' },
    method,
    ...params,
  }
  if (htcToken) {
    init.headers = {
      ...init.headers,
      ...{ authkey: htcToken },
    }
  }
  if (payload) {
    init.body = JSON.stringify(payload)
  }

  const marketBaseURL = getMarketBaseURL()

  return fetch(`${marketBaseURL}${url}`, init)
    .then(async (response) => {
      const result = await response.text()
      try {
        return { status: response.status, results: JSON.parse(result), headers: response.headers }
      } catch (e) {
        // Don't return json.
        return { status: response.status, results: result, headers: response.headers }
      }
    })
    .catch((error) => {
      console.error('fetchMarketAPI error:', error)
    })
}

const GQL_PLAN_LIST_QUERY = `
  query ($pageSize: Int, $currentPage: Int, $creator_account_id: String) {
    products(
      pageSize: $pageSize
      currentPage: $currentPage
      filter: { creator_account_ids: { match: $creator_account_id } }
    ) {
      total_count
      page_info { current_page page_size total_pages }
      items { id sku creator_account_ids payment_type product_owner}
    }
  }
`
  .replace(/\s+/g, ' ')
  .trim()

export const getPlanList = (creatorAccountId: string, pageSize = 50, currentPage = 1) => {
  const url = buildGraphqlGetUrl(`${API_GET_MARKET_PLAN_LIST}`, GQL_PLAN_LIST_QUERY, {
    pageSize,
    currentPage,
    creator_account_id: creatorAccountId,
  })
  return fetchMarketAPI(url, 'GET', null, {})
}

const toProductIdsParam = (ids: string | string[]) => {
  const arr = Array.isArray(ids) ? ids : [ids]
  return arr.map((x) => encodeURIComponent(x)).join(',')
}

export const getPlanDetail = (planIds: string | string[], locale?: Locale) => {
  const storeCode = mapLocaleToStoreCode(locale)
  const url = `/rest/${storeCode}/V1/api/nft/v1/product/detail/list?productIds=${toProductIdsParam(planIds)}`
  return fetchMarketAPI(url, 'GET', null, {})
}

export const getTipAmountList = () => {
  const url = '/rest/V1/api/tip/amount'
  return fetchMarketAPI<TipAmountResponse>(url, 'GET', null, { credentials: 'include' })
}
